import { ACTION_TYPE } from "../core/redux/actionType";
import { AccessRuleChangeRequest } from "./accessRules/requests/accessRuleRequestBuilder";
import { saveLiftAccessRulesAction } from "./saveLiftAccessRulesAction";
import {
  LiftAccessRuleBatchSaveResult,
  LiftAccessRuleSaveResult,
} from "./models";
import { REQUEST_NOT_SENT_CODE } from "./accessRules/saveErrorCodes";
import { extractApiError } from "./accessRules/liftAccessRuleSet/bulkResults";

/**
 * A bulk edit is one request per rule set — save-changes takes a single context, unlike
 * the multi-context add and delete endpoints — so these numbers set how long a large
 * edit takes.
 *
 * Concurrency is tuned for throughput, not gentleness: the breaker counts responses,
 * not requests in flight, so a failing wave of 6 overshoots failureMax (3) once before
 * the next wave bails out at dispatch. Any success zeroes the count anyway.
 *
 * Keep the stagger at 0: it is awaited before each dispatch, so it delays the whole
 * wave by (concurrency - 1) × itself and scales against the concurrency it looks like
 * it is smoothing.
 *
 * TODO: Make these global settings (Phase 2), mirroring the bulk-add batch config.
 */
const BULK_SAVE_LIFT_ACCESS_RULES_BATCH_CONFIG = {
  defaultConcurrency: 6,
  /** A throttle, so a long run doesn't hold the API at full depth throughout. */
  defaultConcurrencyDelayMs: 50,
  defaultBatchDelayMs: 0,
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * One save request, normalized to a success/failure flag. Two failure shapes reach
 * here: an HTTP error, and a 2xx response carrying `success: false`.
 */
const dispatchSingleSave =
  (saveRequest: AccessRuleChangeRequest) =>
  async (dispatch: any): Promise<LiftAccessRuleSaveResult> => {
    try {
      const result = await dispatch(saveLiftAccessRulesAction(saveRequest));

      // undefined = the middleware bailed out (breaker open), so nothing was sent.
      // Checked explicitly: the success test below reads undefined as a success.
      if (!result || typeof result !== "object") {
        return {
          success: false,
          context: saveRequest.Context,
          errorCode: REQUEST_NOT_SENT_CODE,
        };
      }

      const failed = !!result.error || result.payload?.success === false;

      // The detail the outcome screen reports, and what decides retryability.
      return {
        success: !failed,
        context: saveRequest.Context,
        ...(failed ? extractApiError(result) : {}),
      };
    } catch (e) {
      console.warn("Bulk save request threw", e);
      return {
        success: false,
        context: saveRequest.Context,
        errorMessage: e instanceof Error ? e.message : undefined,
      };
    }
  };

/**
 * Fans the per-rule-set save requests through a concurrency-limited runner,
 * reporting progress through the slice the bulk-add flow uses.
 *
 * Pass only rule sets the review scan marked as changing: a no-op set returns
 * success and inflates the applied count.
 */
export const bulkSaveLiftAccessRulesBatched =
  (
    saveRequests: AccessRuleChangeRequest[],
    options?: { cancelRef?: { cancelled: boolean }; concurrency?: number },
  ) =>
  async (dispatch: any): Promise<LiftAccessRuleBatchSaveResult> => {
    // By reference, so a cancel raised mid-run is seen without restarting the loop.
    const cancelRef = options?.cancelRef ?? { cancelled: false };
    const concurrency =
      options?.concurrency ??
      BULK_SAVE_LIFT_ACCESS_RULES_BATCH_CONFIG.defaultConcurrency;

    const total = saveRequests.length;

    let processed = 0;
    let runningSuccessCount = 0;
    let runningFailureCount = 0;
    // Once one request comes back never-sent the breaker stays open for its
    // cool-down, so the rest would bail out too. Stop and report the remainder as
    // not attempted rather than as failures.
    let stoppedNotSent = false;
    const allResults: LiftAccessRuleSaveResult[] = [];

    dispatch({
      type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_PROGRESS,
      payload: { current: 0, total, successCount: 0, failureCount: 0 },
    });

    for (let i = 0; i < saveRequests.length; i += concurrency) {
      // Checked between waves: requests already dispatched finish, and their saves
      // stand. Stopping is not starting more, never undoing what landed.
      if (cancelRef.cancelled) break;

      const slice = saveRequests.slice(i, i + concurrency);
      const promises: Promise<LiftAccessRuleSaveResult>[] = [];

      for (let k = 0; k < slice.length; k++) {
        // Zero by default; the guard saves a macrotask per request.
        if (k > 0 && BULK_SAVE_LIFT_ACCESS_RULES_BATCH_CONFIG.defaultBatchDelayMs > 0) {
          await delay(
            BULK_SAVE_LIFT_ACCESS_RULES_BATCH_CONFIG.defaultBatchDelayMs,
          );
        }
        promises.push(dispatch(dispatchSingleSave(slice[k])));
      }

      const results = await Promise.all(promises);

      for (const result of results) {
        if (result?.success) {
          runningSuccessCount += 1;
        } else {
          runningFailureCount += 1;
          if (result?.errorCode === REQUEST_NOT_SENT_CODE) stoppedNotSent = true;
        }
        allResults.push(result);
        processed += 1;
      }

      dispatch({
        type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_PROGRESS,
        payload: {
          current: processed,
          total,
          successCount: runningSuccessCount,
          failureCount: runningFailureCount,
        },
      });

      if (stoppedNotSent) {
        console.warn(
          "Bulk save stopped: the API stopped accepting requests (circuit breaker open)",
        );
        break;
      }

      // Skipped once cancelled: the loop is about to break, and the pause would only
      // hold the card on "Cancelling..." for no reason.
      if (
        !cancelRef.cancelled &&
        BULK_SAVE_LIFT_ACCESS_RULES_BATCH_CONFIG.defaultConcurrencyDelayMs > 0
      ) {
        await delay(
          BULK_SAVE_LIFT_ACCESS_RULES_BATCH_CONFIG.defaultConcurrencyDelayMs,
        );
      }
    }

    const combinedResult: LiftAccessRuleBatchSaveResult = {
      results: allResults,
      successCount: runningSuccessCount,
      failureCount: runningFailureCount,
      hasFailures: runningFailureCount > 0,
      cancelled: cancelRef.cancelled,
      notAttempted: Math.max(total - processed, 0),
    };

    dispatch({
      type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_COMPLETE,
      payload: combinedResult,
    });

    return combinedResult;
  };
